import type { QueryKey } from '@tanstack/react-query';

import { ApiError } from '@/shared/api/client';

/**
 * Which session a failed READ says has ended — or null when it says nothing
 * about one (w9-to-w6-1).
 *
 * A 401/419 (`UNAUTHENTICATED`) on a data read means the session is gone under
 * the page — expired, signed out in another tab. The shell must not stay on
 * screen with Help, Quick Find and cached data in it until somebody reloads:
 * the session is asked again, and its own gate shows the sign-in.
 *
 * - The path decides which session, as the MFA guard does: `/platform/…` is
 *   the operator's, `/vendor/…` the supplier's, anything else the tenant's.
 * - A session read answering 401 is that session's own answer ("signed out"),
 *   not an ending; token reads (activation, secure link) need no session.
 */
const TENANT_SESSION: QueryKey = ['session'];
const VENDOR_SESSION: QueryKey = ['vendor', 'session'];
const PLATFORM_SESSION: QueryKey = ['platform', 'session'];

export function sessionEndedBy(queryKey: QueryKey, error: unknown): QueryKey | null {
  if (!(error instanceof ApiError)) return null;
  if (!(error.status === 401 || error.status === 419 || error.isUnauthenticated)) return null;

  const [head, second] = queryKey;

  if (head === 'session' || second === 'session') return null;
  if (head === 'activation' || head === 'secure-action') return null;

  const path = error.path ?? '';

  if (path === '/platform' || path.startsWith('/platform/')) return PLATFORM_SESSION;
  if (path === '/vendor' || path.startsWith('/vendor/')) return VENDOR_SESSION;

  return TENANT_SESSION;
}
